/**
 * A custom React hook that manages scroll behavior for a Tiptap editor.
 * This hook ensures that the editor content remains visible and properly positioned
 * during user interaction, particularly when typing or navigating through content.
 */

import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef } from "react";

/**
 * Configuration options for the scroll adjustment hook
 * @interface UseTiptapScrollAdjustmentOptions
 * @property {Editor | null} editor - The Tiptap editor instance to manage
 * @property {number} [offset=100] - The minimum distance (in pixels) to maintain between the editor and viewport bottom
 * @property {ScrollBehavior} [behavior='smooth'] - The scroll behavior to use ('auto' or 'smooth')
 * @property {number} [debounceDelay=100] - Delay in milliseconds before applying scroll adjustments
 * @property {boolean} [scrollToHiddenAnchor=false] - Whether to scroll to a hidden anchor element instead of the editor
 */
interface UseTiptapScrollAdjustmentOptions {
  editor: Editor | null;
  offset?: number;
  behavior?: ScrollBehavior;
  debounceDelay?: number;
  scrollToHiddenAnchor?: boolean;
}

/**
 * Custom hook that manages scroll behavior for a Tiptap editor
 * @param {UseTiptapScrollAdjustmentOptions} options - Configuration options for the scroll behavior
 */
const useScrollHiddenTiptap = (options: UseTiptapScrollAdjustmentOptions) => {
  // Destructure and set default values for options
  const {
    editor,
    offset = 100,
    behavior = "smooth",
    debounceDelay = 100,
    scrollToHiddenAnchor = false,
  } = options;

  // Refs to track state across renders
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Stores the debounce timeout
  const isUserScrolling = useRef(false); // Tracks if user is manually scrolling
  const isInputting = useRef(false); // Tracks if user is currently typing
  const isCleanupFunctionRunning = useRef(false);

  const isEditorMounted = useCallback(() => {
    try {
      return editor != null && editor.view != null && editor.view.dom != null;
    } catch {
      return false;
    }
  }, [editor]);

  /**
   * Scrolls an element into view with a specified offset from the viewport bottom
   * @param {HTMLElement} element - The element to scroll into view
   * @param {boolean} force - Whether to force scroll regardless of user scrolling state
   */
  const scrollIntoViewWithOffset = useCallback(
    (element: HTMLElement, force = false) => {
      if (isCleanupFunctionRunning.current) return;
      // Don't scroll if user is manually scrolling or typing (unless forced)
      if (!force && isUserScrolling.current) return;

      // Calculate element position and viewport dimensions
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isObscured = rect.bottom > viewportHeight - offset;
      const isElementLargerThanViewport = rect.height > viewportHeight;

      if (isObscured) {
        // Choose scroll behavior based on element size
        const decideBlockType: ScrollIntoViewOptions["block"] =
          isElementLargerThanViewport ? "end" : "center";

        // only read editor.isFocused when view is still mounted
        const editorFocused = isEditorMounted()
          ? Boolean(editor?.isFocused)
          : false;

        // Handle scrolling to either hidden anchor or the editor element
        const hiddenAnchor = document.getElementById("hidden-anchor");
        if (scrollToHiddenAnchor && hiddenAnchor) {
          // prevents the page from jumping back down to the editor when trying to scroll up
          if (!editorFocused) {
            hiddenAnchor.scrollIntoView({ behavior, block: decideBlockType });
          }
        } else {
          // prevents the page from jumping back down to the editor when trying to scroll up
          if (!editorFocused) {
            element.scrollIntoView({ behavior, block: decideBlockType });
          }
        }
      }
    },
    [scrollToHiddenAnchor, offset, behavior, editor, isEditorMounted],
  );

  /**
   * Debounced version of scrollIntoViewWithOffset to prevent excessive scrolling
   * @param {HTMLElement} element - The element to scroll into view
   */
  const debouncedScroll = useCallback(
    (element: HTMLElement) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        isInputting.current = false;
      }

      timeoutRef.current = setTimeout(() => {
        if (isCleanupFunctionRunning.current) return;
        scrollIntoViewWithOffset(element, true);
      }, debounceDelay);
    },
    [debounceDelay, scrollIntoViewWithOffset],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch debouncedScroll
  useEffect(() => {
    try {
      if (!editor?.view?.dom) return;
    } catch {
      return;
    }

    // ensure reset of cleanup function flag
    isCleanupFunctionRunning.current = false;
    const editorElement = editor.view.dom;

    /**
     * Handles window scroll events to detect user-initiated scrolling
     */
    const handleScroll = () => {
      if (!isInputting.current) {
        isUserScrolling.current = true;
      }
    };

    /**
     * Detects user input and navigation to manage scroll behavior
     * @param {KeyboardEvent} event - The keyboard event
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isInputting.current) {
        const isNavigationKey = [
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
        ].includes(event.key);
        // True if the key is a single character, backspace, or delete
        const isTypingKey =
          event.key.length === 1 ||
          event.key === "Backspace" ||
          event.key === "Delete";

        if (isNavigationKey || isTypingKey) {
          isInputting.current = true;
        }
      }
    };

    /**
     * Handles editor focus events to ensure visibility
     */
    const handleFocus = () => {
      if (isCleanupFunctionRunning.current) return;
      isInputting.current = true;
      scrollIntoViewWithOffset(editorElement, true);
    };

    const handleCreate = () => {
      if (isCleanupFunctionRunning.current) return;
      scrollIntoViewWithOffset(editorElement, true);
    };

    // Set up intersection observer to detect when editor goes out of view
    const observer = new IntersectionObserver(
      (entries) => {
        if (isCleanupFunctionRunning.current) return;
        // avoid accessing editor.isFocused when view may be destroyed
        if (!isEditorMounted()) return;
        for (const entry of entries) {
          if (
            !entry.isIntersecting &&
            editor.isFocused &&
            isInputting.current
          ) {
            scrollIntoViewWithOffset(editorElement, true);
          }
        }
      },
      {
        root: null,
        rootMargin: `-${offset}px 0px 0px 0px`, // Creates a margin at the bottom of the viewport
        threshold: 1.0, // Only trigger when element is fully visible/invisible
      },
    );

    // Start observing the editor element
    observer.observe(editorElement);

    // Set up event listeners
    window.addEventListener("scroll", handleScroll, { passive: true });
    editorElement.addEventListener("keydown", handleKeyDown);
    editor.on("focus", handleFocus);
    editor.on("create", handleCreate);

    // Cleanup function to remove event listeners and observers
    return () => {
      isCleanupFunctionRunning.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
      if (editorElement && document.contains(editorElement)) {
        editorElement.removeEventListener("keydown", handleKeyDown);
      }
      try {
        if (editor?.view != null) {
          editor.off("focus", handleFocus);
          editor.off("create", handleCreate);
        }
      } catch {
        // editor may already be destroyed
      }
    };
  }, [
    editor,
    offset,
    debouncedScroll,
    scrollIntoViewWithOffset,
    isEditorMounted,
  ]);
};

export default useScrollHiddenTiptap;
